import React from 'react';
import { Settings } from 'lucide-react';
import type { ProductTemplate } from '../../utils/templates/productTemplates';

interface TemplateSelectorProps {
  templates: ProductTemplate[];
  selectedTemplate: ProductTemplate | null;
  onSelect: (template: ProductTemplate | null) => void;
  onEdit?: (template: ProductTemplate) => void;
  /** Smaller sizing for bulk import modal (mobile-friendly) */
  compact?: boolean;
}

export function TemplateSelector({ 
  templates, 
  selectedTemplate, 
  onSelect,
  onEdit,
  compact = false
}: TemplateSelectorProps) {
  const selectClass = compact
    ? 'flex-1 min-w-0 h-10 sm:h-11 px-3 rounded-lg text-sm sm:text-base font-medium border-2 border-indigo-500 bg-white sm:bg-gradient-to-r sm:from-indigo-50 sm:via-purple-50 sm:to-pink-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer shadow-sm disabled:opacity-70 disabled:cursor-not-allowed truncate'
    : `flex-1 h-14 px-4 rounded-lg text-lg font-medium border-2 border-indigo-500 
          bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50
          hover:from-indigo-100 hover:via-purple-100 hover:to-pink-100
          focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
          transition-all duration-300 cursor-pointer shadow-sm
          hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed`;

  return (
    <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
      <select
        value={selectedTemplate?.id || ''}
        onChange={(e) => {
          const template = templates.find(t => t.id === e.target.value);
          onSelect(template || null);
        }}
        disabled={templates.length === 0}
        className={selectClass}
      >
        <option value="" className="text-sm sm:text-base bg-white text-gray-500">
          {templates.length === 0
            ? '保存されたテンプレートはありません'
            : 'テンプレートを選択してください'}
        </option>
        {templates.map(template => (
          <option 
            key={template.id} 
            value={template.id}
            className="text-sm sm:text-base bg-white text-gray-900"
          >
            {template.name}
          </option>
        ))}
      </select>
      {onEdit && (
        <button
          type="button"
          onClick={() => {
            if (selectedTemplate) {
              onEdit(selectedTemplate);
            } else if (templates.length > 0) {
              const firstTemplate = templates[0];
              onSelect(firstTemplate);
              onEdit(firstTemplate);
            }
          }}
          className={`shrink-0 p-1.5 sm:p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors ${
            templates.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title="テンプレートの値を編集"
          disabled={templates.length === 0}
        >
          <Settings className={compact ? 'w-5 h-5 sm:w-6 sm:h-6' : 'w-6 h-6'} />
        </button>
      )}
    </div>
  );
}